import React, { useState, useEffect } from 'react';
import { X, Car, AlertCircle, ChevronDown, Loader2, Sparkles, CheckCircle2, ScanLine } from 'lucide-react';
import { CustomerVehicle, updateCustomerVehicle } from '../../lib/customers';
import { decodeVin, fetchTrimOptions, isPlausibleVin, vinDecodeSummary } from '../../lib/vinDecode';
import { useBusiness } from '../../contexts/BusinessContext';
import VinScanModal from '../VinScanModal';

interface EditVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (vehicle: CustomerVehicle) => void;
  vehicle: CustomerVehicle | null;
  /** VIN just captured by a photo scan elsewhere (e.g. booking modal) — prefills the field and decodes immediately on open. */
  initialScannedVin?: string | null;
}

export default function EditVehicleModal({
  isOpen,
  onClose,
  onUpdate,
  vehicle,
  initialScannedVin
}: EditVehicleModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isAdditionalExpanded, setIsAdditionalExpanded] = useState(false);
  const [formData, setFormData] = useState<{
    make: string;
    model: string;
    year: number;
    trim: string;
    vin: string;
    color: string;
    paint_code: string;
    license_plate: string;
    mileage: number;
    notes: string;
    is_primary: boolean;
  }>({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    trim: '',
    vin: '', 
    color: '', 
    paint_code: '',
    license_plate: '', 
    mileage: 0,
    notes: '',
    is_primary: false
  });
  const { selectedBusiness } = useBusiness();
  const [decoding, setDecoding] = useState(false);
  const [decodeSummary, setDecodeSummary] = useState<string | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [vinScanOpen, setVinScanOpen] = useState(false);
  const [trimOptions, setTrimOptions] = useState<string[]>([]);
  // VIN that is already decoded (saved on the vehicle or decoded this session) —
  // while the field still holds it, Decode/Scan are greyed out; clearing or
  // changing the VIN re-enables them.
  const [decodedVin, setDecodedVin] = useState<string | null>(null);
  const vinLocked = !!decodedVin && formData.vin.trim().toUpperCase() === decodedVin;

  /** Load the trim lineup for the dropdown; empty result keeps free-text entry. */
  const loadTrimOptions = (year: number | null, make: string | null, model: string | null) => {
    if (!year || !make || !model) return;
    fetchTrimOptions(year, make, model)
      .then((trims) => {
        setTrimOptions(trims);
        if (trims.length === 0) return;
        // Snap a messy decoded trim (e.g. "XSE (Natl)") onto its real lineup entry.
        setFormData(prev => {
          const cur = prev.trim.trim();
          if (!cur || trims.includes(cur)) return prev;
          const lower = cur.toLowerCase();
          const match =
            trims.find(t => t.toLowerCase() === lower) ||
            [...trims]
              .sort((a, b) => b.length - a.length)
              .find(t => lower.startsWith(t.toLowerCase()));
          return match ? { ...prev, trim: match } : prev;
        });
      })
      .catch(() => {});
  };

  const runDecode = async (vin: string) => {
    setDecodeError(null);
    setDecodeSummary(null);
    if (!isPlausibleVin(vin)) {
      setDecodeError('Enter the full 17-character VIN first.');
      return;
    }
    setDecoding(true);
    try {
      const d = await decodeVin(vin);
      if (!d.valid) {
        setDecodeError(d.error || 'This VIN could not be decoded.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        make: d.make || prev.make,
        model: d.model || prev.model,
        year: d.year || prev.year,
        trim: d.trim || prev.trim,
        color: d.exteriorColor || prev.color,
        paint_code: d.paintCode || prev.paint_code,
      }));
      setDecodeSummary(vinDecodeSummary(d));
      setDecodedVin(vin.trim().toUpperCase());
      loadTrimOptions(d.year, d.make, d.model);
    } catch (err) {
      console.error('VIN decode failed:', err);
      setDecodeError('Decode failed — check your connection and try again.');
    } finally {
      setDecoding(false);
    }
  };

  const handleDecodeVin = () => void runDecode(formData.vin);

  /** Photo scan captured a VIN — fill the field and decode immediately. */
  const handleVinScanned = (vin: string) => {
    setFormData(prev => ({ ...prev, vin }));
    void runDecode(vin);
  };

  useEffect(() => {
    if (isOpen && vehicle) {
      setIsAnimating(true);
      setFormData({
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        trim: vehicle.trim || '',
        vin: vehicle.vin || '',
        color: vehicle.color || '',
        paint_code: vehicle.paint_code || '',
        license_plate: vehicle.license_plate || '',
        mileage: vehicle.mileage || 0,
        notes: vehicle.notes || '',
        is_primary: vehicle.is_primary || false
      });
      const hasAdditional = !!(vehicle.color || vehicle.paint_code || vehicle.vin || vehicle.license_plate || vehicle.notes);
      setIsAdditionalExpanded(hasAdditional);
      setDecodeSummary(null);
      setDecodeError(null);
      setDecodedVin(vehicle.vin && isPlausibleVin(vehicle.vin) ? vehicle.vin.trim().toUpperCase() : null);
      // Offer the trim dropdown right away when the vehicle identity is known.
      setTrimOptions([]);
      loadTrimOptions(vehicle.year || null, vehicle.make || null, vehicle.model || null);

      // Opened straight from a photo scan: show the VIN and decode right away
      // so the user watches it process here instead of behind a toast.
      if (initialScannedVin) {
        const scanned = initialScannedVin.trim().toUpperCase();
        setFormData(prev => ({ ...prev, vin: scanned }));
        setIsAdditionalExpanded(true);
        setDecodedVin(null);
        void runDecode(scanned);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vehicle]);

  useEffect(() => {
    setIsFormValid(formData.make.trim() !== '' && formData.model.trim() !== '');
  }, [formData.make, formData.model]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicle) return;

    try {
      setLoading(true);
      setError(null);

      const updatedVehicle = await updateCustomerVehicle(vehicle.id, formData);
      onUpdate(updatedVehicle);
      onClose();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to update vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!vehicle) return null;

  return (
    <div 
      className={`fixed inset-0 transition-all duration-300 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100100,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100100,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Car className="h-5 w-5 text-gray-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Edit Vehicle
            </h2>
            {vehicle && (
              <p className="text-sm text-gray-500 mt-1">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </p>
            )}
          </div>

          {/* Form Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-5 space-y-4">
              {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-red-700">{error}</span>
                </div>
              )}

              {/* Basic Information */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Basic Information</span>
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="px-4 py-3 flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-500 w-20 flex-shrink-0">Make *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Toyota"
                      value={formData.make}
                      onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                      className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="px-4 py-3 flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-500 w-20 flex-shrink-0">Model *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Camry"
                      value={formData.model}
                      onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                      className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="px-4 py-3 flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-500 w-20 flex-shrink-0">Year</label>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      value={formData.year || new Date().getFullYear()}
                      onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                      className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="px-4 py-3 flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-500 w-20 flex-shrink-0">Trim</label>
                    {trimOptions.length > 0 ? (
                      <select
                        value={formData.trim}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') {
                            // Switch back to free-text entry, keeping the current value.
                            setTrimOptions([]);
                            return;
                          }
                          setFormData(prev => ({ ...prev, trim: e.target.value }));
                        }}
                        className="flex-1 min-w-0 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 appearance-none"
                        style={{ fontSize: '16px' }}
                      >
                        <option value="">Select trim</option>
                        {/* Keep a decoded/typed trim selectable even if it's not in the lineup */}
                        {formData.trim && !trimOptions.includes(formData.trim) && (
                          <option value={formData.trim}>{formData.trim}</option>
                        )}
                        {trimOptions.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value="__custom__">Other (type manually)</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder="e.g. Limited"
                        value={formData.trim}
                        onChange={(e) => setFormData(prev => ({ ...prev, trim: e.target.value }))}
                        className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300"
                        style={{ fontSize: '16px' }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Details — Collapsible */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div
                  className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 transition-colors active:scale-[0.99] touch-manipulation"
                  onClick={() => setIsAdditionalExpanded(!isAdditionalExpanded)}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Additional Details</span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isAdditionalExpanded ? '' : '-rotate-90'}`} />
                </div>
                {isAdditionalExpanded && (
                  <div className="divide-y divide-gray-100 border-t border-gray-100">
                    <div className="px-4 py-3 flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">Color</label>
                      <input
                        type="text"
                        placeholder="e.g. White"
                        value={formData.color}
                        onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">Paint Code</label>
                      <input
                        type="text"
                        placeholder="e.g. 1F7"
                        value={formData.paint_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, paint_code: e.target.value.toUpperCase() }))}
                        className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300 font-mono uppercase"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div className="px-4 py-3">
                      <label className="text-sm font-medium text-gray-500 mb-2 block">VIN</label>
                      {/* Full-width VIN input with the two actions attached below */}
                      <div className="rounded-xl border border-gray-200 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-150">
                        <input
                          type="text"
                          placeholder="Enter VIN"
                          value={formData.vin}
                          maxLength={17}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, vin: e.target.value.toUpperCase() }));
                            setDecodeSummary(null);
                            setDecodeError(null);
                          }}
                          className="w-full px-3 py-2.5 text-sm font-medium text-gray-900 border-0 focus:outline-none focus:ring-0 placeholder:text-gray-300 font-mono uppercase"
                          style={{ fontSize: '16px' }}
                        />
                        <div className="grid grid-cols-2 border-t border-gray-200 divide-x divide-gray-200">
                          <button
                            type="button"
                            onClick={handleDecodeVin}
                            disabled={decoding || vinLocked || !isPlausibleVin(formData.vin)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-900 text-xs font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:bg-gray-200 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {decoding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                            Decode
                          </button>
                          <button
                            type="button"
                            onClick={() => setVinScanOpen(true)}
                            disabled={decoding || vinLocked}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 text-blue-600 text-xs font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:bg-gray-200 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <ScanLine className="h-3.5 w-3.5" />
                            Scan
                          </button>
                        </div>
                      </div>
                      {decodeSummary && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                          <span>{decodeSummary}</span>
                        </p>
                      )}
                      {decodeError && (
                        <p className="mt-2 text-xs text-red-600">{decodeError}</p>
                      )}
                      {vinLocked && !decodeSummary && !decodeError && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                          <span>VIN decoded — clear the field to enter or scan a different VIN.</span>
                        </p>
                      )}
                    </div>
                    <div className="px-4 py-3 flex items-center gap-3">
                      <label className="text-sm font-medium text-gray-500 w-24 flex-shrink-0">Plate</label>
                      <input
                        type="text"
                        placeholder="Enter license plate"
                        value={formData.license_plate}
                        onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                        className="flex-1 text-sm font-medium text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-gray-300"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div className="px-4 py-3">
                      <label className="text-sm font-medium text-gray-500 mb-2 block">Notes</label>
                      <textarea
                        placeholder="Add any notes about this vehicle..."
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none placeholder:text-gray-300"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Primary Vehicle Toggle */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="px-4 py-3.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Primary vehicle</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, is_primary: !prev.is_primary }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                      formData.is_primary ? 'bg-blue-500' : 'bg-gray-200'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ease-in-out ${
                        formData.is_primary ? 'translate-x-[22px]' : 'translate-x-[2px]'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </form>

          {/* Fixed Footer */}
          <div className="border-t border-gray-200 bg-white px-5 py-4 flex-shrink-0">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !isFormValid}
              className={`w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:cursor-not-allowed disabled:transform-none ${
                loading || !isFormValid
                  ? 'bg-gray-300'
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>

      <VinScanModal
        isOpen={vinScanOpen}
        businessId={selectedBusiness?.id || ''}
        onClose={() => setVinScanOpen(false)}
        onScanned={handleVinScanned}
      />
    </div>
  );
}
